import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar, CheckCircle2, CreditCard, MessageCircle } from "lucide-react";
import { toast } from "react-toastify";
import SEO from "../components/SEO";
import PageHero from "../components/common/PageHero";
import {
  SERVICES,
  TIME_SLOTS,
  formatBookingDate,
  getAvailableDates,
  getServiceBySlug,
} from "../data/services";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  WHATSAPP_URL,
} from "../constants/social";

export default function BookSessionPage() {
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get("service") || "coaching";

  const availableDates = useMemo(() => getAvailableDates(20), []);
  const [form, setForm] = useState({
    service: getServiceBySlug(initialService) ? initialService : "coaching",
    packageId: "",
    date: "",
    time: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [step, setStep] = useState(1);

  const selectedService = getServiceBySlug(form.service);
  const selectedPackage =
    selectedService?.packages.find((pkg) => pkg.id === form.packageId) ||
    selectedService?.packages[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (selectedService && !form.packageId) {
      setForm((prev) => ({
        ...prev,
        packageId: selectedService.packages[0]?.id || "",
      }));
    }
  }, [selectedService, form.packageId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "service") {
        const svc = getServiceBySlug(value);
        next.packageId = svc?.packages[0]?.id || "";
      }
      return next;
    });
  };

  const bookingSummary = () => {
    const dateLabel = form.date
      ? formatBookingDate(new Date(form.date))
      : "Date TBC";
    return [
      `Service: ${selectedService?.title || form.service}`,
      `Package: ${selectedPackage?.name || "TBC"} (${selectedPackage?.duration || ""})`,
      `Investment: ${selectedPackage?.priceLabel || "Custom quote"}`,
      `Preferred date: ${dateLabel}`,
      `Preferred time (UK): ${form.time || "TBC"}`,
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone || "Not provided"}`,
      `Notes: ${form.notes || "None"}`,
    ].join("\n");
  };

  const validateStep1 = () => {
    if (!form.service || !form.packageId || !form.date || !form.time) {
      toast.error("Please choose a service, package, date, and time.");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return false;
    }
    return true;
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent(
      `Hello Wisdom Peters — I'd like to book a session.\n\n${bookingSummary()}`
    );
    window.open(`https://wa.me/447518576657?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const openEmail = () => {
    const subject = encodeURIComponent(
      `Session booking request — ${selectedService?.shortTitle || "Wisdom Peters"}`
    );
    const body = encodeURIComponent(
      `I'd like to book the following session:\n\n${bookingSummary()}\n\nPlease confirm availability and send a Stripe payment link / invoice.`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleConfirm = (channel) => {
    if (!validateStep2()) return;
    setStep(3);
    if (channel === "whatsapp") openWhatsApp();
    if (channel === "email") openEmail();
    toast.success("Booking request prepared. Complete payment after confirmation.");
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <SEO
        title="Book a Session | Wisdom Peters"
        description="Choose an available date and time to book coaching, consulting, mentorship, or speaking with Wisdom Peters."
        name="Wisdom Peters"
        type="website"
      />

      <PageHero
        eyebrow="Appointments"
        title="Book a Session"
        subtitle="Select an available date, confirm details, then pay securely via Stripe after confirmation."
        backgroundImage="/pl 4.jpeg"
        overlayClass="bg-gradient-to-br from-black/80 via-purple-950/75 to-black/85"
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Services", to: "/services" },
          { label: "Book a Session" },
        ]}
      />

      <div className="container mx-auto px-4 py-12 max-w-5xl -mt-2 relative z-20">
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { n: 1, label: "Date & package" },
            { n: 2, label: "Your details" },
            { n: 3, label: "Confirm & pay" },
          ].map(({ n, label }) => (
            <div
              key={n}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                step >= n
                  ? "bg-purple-700 text-white border-purple-700"
                  : "bg-white text-slate-500 border-slate-200"
              }`}
            >
              {n}. {label}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-8">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Service
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                {SERVICES.map((svc) => (
                  <label
                    key={svc.slug}
                    className={`border rounded-xl p-4 cursor-pointer transition ${
                      form.service === svc.slug
                        ? "border-purple-500 bg-purple-50"
                        : "border-slate-200 hover:border-purple-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="service"
                      value={svc.slug}
                      checked={form.service === svc.slug}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="font-semibold text-slate-800 block">
                      {svc.shortTitle}
                    </span>
                    <span className="text-xs text-slate-500">{svc.summary}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Package
              </label>
              <select
                name="packageId"
                value={form.packageId}
                onChange={handleChange}
                className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm"
              >
                {selectedService?.packages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} — {pkg.priceLabel} ({pkg.duration})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Available dates
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
                {availableDates.map((date) => {
                  const value = date.toISOString().slice(0, 10);
                  return (
                    <button
                      type="button"
                      key={value}
                      onClick={() => setForm((prev) => ({ ...prev, date: value }))}
                      className={`text-left text-sm rounded-lg border px-3 py-2 transition ${
                        form.date === value
                          ? "border-purple-600 bg-purple-50 text-purple-900"
                          : "border-slate-200 hover:border-purple-200"
                      }`}
                    >
                      {formatBookingDate(date)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Preferred time (UK)
              </label>
              <div className="flex flex-wrap gap-2">
                {TIME_SLOTS.map((slot) => (
                  <button
                    type="button"
                    key={slot}
                    onClick={() => setForm((prev) => ({ ...prev, time: slot }))}
                    className={`px-3 py-2 rounded-md text-sm border transition ${
                      form.time === slot
                        ? "bg-purple-700 text-white border-purple-700"
                        : "border-slate-200 hover:border-purple-300"
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (validateStep1()) setStep(2);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-700 text-white font-semibold rounded-md hover:bg-purple-800 transition"
            >
              <Calendar className="w-4 h-4" />
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-5">
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-800 mb-1">Your selection</p>
              <p>
                {selectedService?.title} · {selectedPackage?.name} ·{" "}
                {form.date ? formatBookingDate(new Date(form.date)) : ""} ·{" "}
                {form.time} UK · {selectedPackage?.priceLabel}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full name *
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone / WhatsApp
              </label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm"
                placeholder={CONTACT_PHONE}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Goals or event details
              </label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={4}
                className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm"
                placeholder="Share context so Wisdom can prepare for your session."
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-5 py-2.5 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => {
                  if (validateStep2()) setStep(3);
                }}
                className="px-5 py-2.5 bg-purple-700 text-white rounded-md text-sm font-semibold hover:bg-purple-800"
              >
                Review & confirm
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
              <div>
                <h2 className="text-xl font-bold text-slate-800">Confirm your booking request</h2>
                <p className="text-slate-500 text-sm mt-1">
                  Send your request via WhatsApp or email. Once the slot is
                  confirmed, you’ll receive a Stripe invoice or payment link to
                  secure the appointment.
                </p>
              </div>
            </div>

            <pre className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-xs sm:text-sm text-slate-700 whitespace-pre-wrap overflow-x-auto">
              {bookingSummary()}
            </pre>

            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleConfirm("whatsapp")}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white font-semibold rounded-md hover:bg-[#1ebe57] transition"
              >
                <MessageCircle className="w-4 h-4" />
                Send via WhatsApp
              </button>
              <button
                type="button"
                onClick={() => handleConfirm("email")}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-purple-700 text-white font-semibold rounded-md hover:bg-purple-800 transition"
              >
                Send via Email
              </button>
            </div>

            <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <CreditCard className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">Payment</p>
                  <p className="mt-1">
                    Payments are processed securely with{" "}
                    <strong>Stripe</strong> (major credit and debit cards). After
                    confirmation you will receive an invoice or checkout link. Do
                    not transfer funds outside official Stripe channels.
                  </p>
                  <p className="mt-2">
                    Questions? Call{" "}
                    <a className="underline" href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}>
                      {CONTACT_PHONE}
                    </a>{" "}
                    or{" "}
                    <a
                      className="underline"
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-5 py-2.5 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50"
              >
                Back
              </button>
              <Link
                to={`/services/${form.service}`}
                className="px-5 py-2.5 text-sm font-medium text-purple-700 hover:underline"
              >
                View service details
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
